import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AI_ENGINE_URL } from '../../utils/apiConfig';
import {
  Typography,
  CircularProgress,
  Button,
  Grid,
  Paper,
  Box,
  Stack,
  Chip,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import ArchiveIcon from '@mui/icons-material/Archive';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import InventoryIcon from '@mui/icons-material/Inventory';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import NoteIcon from '@mui/icons-material/Note';

/**
 * ArchiveViewer - Read-only viewer for archived reservations
 * Fetches data from AI Engine's legacy archive endpoint
 */
export default function ArchiveViewer() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [archiveData, setArchiveData] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [draaidagInfo, setDraaidagInfo] = useState(null);
  const [reservationInfo, setReservationInfo] = useState(null);

  // Fetch archived data from AI Engine
  useEffect(() => {
    const fetchArchiveData = async () => {
      if (!id) {
        setError('Geen archief ID opgegeven');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Fetch from AI Engine legacy archive endpoint
        const response = await axios.get(
          `${AI_ENGINE_URL}/api/legacy/draaidag-data/${id}`
        );

        const data = response.data;
        setArchiveData(data);

        // Extract draaidag info
        if (data.draaidag) {
          setDraaidagInfo(data.draaidag);
        }

        // Extract reservation info
        if (data.reservation) {
          setReservationInfo(data.reservation);
        }

        // Extract materials
        if (data.materials && Array.isArray(data.materials)) {
          setMaterials(data.materials);
        } else if (data.junctionData && Array.isArray(data.junctionData)) {
          // Try to extract from junction data if materials not directly available
          setMaterials(data.junctionData);
        }

      } catch (err) {
        console.error('Error fetching archive data:', err);
        if (err.response?.status === 404) {
          setError(`Archief record met ID "${id}" niet gevonden.`);
        } else {
          setError(err.response?.data?.detail || err.message || 'Fout bij ophalen archief data');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchArchiveData();
  }, [id]);

  // Parse draaidag display info
  const draaidagDisplay = useMemo(() => {
    if (!draaidagInfo?.fields) return null;

    const fields = draaidagInfo.fields;
    return {
      title: fields.WeergaveNaamMateriaalApp || fields.Draaidag || 'Onbekende draaidag',
      originalTitle: fields.Draaidag || '',
      startDate: fields.StartDatum || fields.Start || fields.StartdatumOutlook,
      endDate: fields.EindDatum || fields.Eind || fields.EinddatumOutlook,
      crew: fields.Camerapersonen || fields.CrewNaam || fields['Crew (from Project)']?.[0] || '',
      projectNummer: fields.ProjectNumRegex || '',
      projectNaam: fields['ProjectNaam (from Project)']?.[0] || fields.ProjectNaam || '',
      projectType: fields.ProjectType?.[0] || fields['Projecttype (from Project)']?.[0] || 'Onbekend',
      geenMateriaalNodig: fields['Geen Materiaal Nodig'] || false
    };
  }, [draaidagInfo]);

  // Parse reservation display info
  const reservationDisplay = useMemo(() => {
    if (!reservationInfo?.fields) return null;

    const fields = reservationInfo.fields;
    return {
      id: reservationInfo.id,
      startDate: fields.Start,
      endDate: fields.Eind,
      notities: fields.Notities || '',
      extraDagVoor: fields.ExtraDagVoor || false,
      extraDagNa: fields.ExtraDagNa || false,
      materialCount: fields.Materiaal?.length || 0
    };
  }, [reservationInfo]);

  // Handle back navigation
  const handleGoBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // Loading state
  if (loading) {
    return (
      <Grid container justifyContent="center" sx={{ mt: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center', minWidth: 300 }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography>Archief data laden...</Typography>
        </Paper>
      </Grid>
    );
  }

  // Error state
  if (error) {
    return (
      <Grid container justifyContent="center" sx={{ mt: 4 }}>
        <Paper sx={{ p: 4, maxWidth: 600 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleGoBack}
          >
            Terug
          </Button>
        </Paper>
      </Grid>
    );
  }

  return (
    <Grid container justifyContent="center">
      <Paper sx={{ p: 3, m: 2, width: '90%', maxWidth: 1200 }}>
        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
          <Button
            variant="text"
            startIcon={<ArrowBackIcon />}
            onClick={handleGoBack}
          >
            Terug
          </Button>
          <Chip
            icon={<ArchiveIcon />}
            label="ARCHIEF - ALLEEN LEZEN"
            color="warning"
            variant="outlined"
          />
        </Stack>

        {/* Draaidag Info Card */}
        {draaidagDisplay && (
          <Card sx={{ mb: 3, bgcolor: '#f5f5f5' }}>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                {draaidagDisplay.title}
              </Typography>
              {draaidagDisplay.originalTitle && draaidagDisplay.originalTitle !== draaidagDisplay.title && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Origineel: {draaidagDisplay.originalTitle}
                </Typography>
              )}
              
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={4}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CalendarTodayIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      <strong>Datum:</strong> {draaidagDisplay.startDate || 'Onbekend'}
                      {draaidagDisplay.endDate && draaidagDisplay.endDate !== draaidagDisplay.startDate && (
                        <> - {draaidagDisplay.endDate}</>
                      )}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={4}>
                  {draaidagDisplay.crew && (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <PersonIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        <strong>Crew:</strong> {draaidagDisplay.crew}
                      </Typography>
                    </Stack>
                  )}
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2">
                    <strong>Type:</strong> {draaidagDisplay.projectType}
                  </Typography>
                  {draaidagDisplay.projectNummer && (
                    <Typography variant="body2">
                      <strong>Project:</strong> [{draaidagDisplay.projectNummer}] {draaidagDisplay.projectNaam}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Reservation Info */}
        {reservationDisplay && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <InventoryIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                Reservering Details
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2">
                    <strong>Reservering ID:</strong> {reservationDisplay.id}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Periode:</strong> {reservationDisplay.startDate} - {reservationDisplay.endDate}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Aantal materialen:</strong> {reservationDisplay.materialCount}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Stack direction="row" spacing={1}>
                    {reservationDisplay.extraDagVoor && (
                      <Chip size="small" label="Extra dag voor" color="info" />
                    )}
                    {reservationDisplay.extraDagNa && (
                      <Chip size="small" label="Extra dag na" color="warning" />
                    )}
                  </Stack>
                </Grid>
              </Grid>

              {reservationDisplay.notities && (
                <Box sx={{ mt: 2, p: 2, bgcolor: '#fffde7', borderRadius: 1 }}>
                  <Stack direction="row" alignItems="flex-start" spacing={1}>
                    <NoteIcon fontSize="small" color="action" />
                    <Box>
                      <Typography variant="subtitle2">Notities:</Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {reservationDisplay.notities}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {/* Materials List */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <InventoryIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
              Gereserveerde Materialen ({materials.length})
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {materials.length === 0 ? (
              <Alert severity="info">
                Geen materialen gevonden in dit archief record.
              </Alert>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>ID</strong></TableCell>
                      <TableCell><strong>Naam</strong></TableCell>
                      <TableCell><strong>Categorie</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {materials.map((material, index) => {
                      const fields = material.fields || material;
                      const id = fields.ID || fields.id || material.id;
                      const naam = fields.Naam || fields.naam || fields.name || '-';
                      const categorie = fields.Categorie || fields.categorie || fields.category || '-';
                      const status = fields.JunctionStatus || fields.StatusJunction || fields.status || [];
                      
                      return (
                        <TableRow key={material.id || index}>
                          <TableCell>{id}</TableCell>
                          <TableCell>{naam}</TableCell>
                          <TableCell>{Array.isArray(categorie) ? categorie[0] : categorie}</TableCell>
                          <TableCell>
                            {Array.isArray(status) && status.length > 0 ? (
                              <Stack direction="row" spacing={0.5}>
                                {status.map((s, i) => (
                                  <Chip 
                                    key={i} 
                                    label={s} 
                                    size="small" 
                                    variant="outlined"
                                    color={
                                      s.toLowerCase().includes('klaar') ? 'success' :
                                      s.toLowerCase().includes('retour') ? 'info' :
                                      'default'
                                    }
                                  />
                                ))}
                              </Stack>
                            ) : (
                              <Typography variant="body2" color="text.secondary">-</Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* Archive Metadata */}
        {archiveData?.archived_at && (
          <Box sx={{ mt: 2, p: 2, bgcolor: '#f0f0f0', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Gearchiveerd op: {new Date(archiveData.archived_at).toLocaleString('nl-NL')}
            </Typography>
          </Box>
        )}

        {/* Debug info (only in development) */}
        {process.env.NODE_ENV === 'development' && archiveData && (
          <Box sx={{ mt: 3 }}>
            <details>
              <summary style={{ cursor: 'pointer', color: '#666' }}>
                Debug: Raw Archive Data
              </summary>
              <pre style={{ 
                fontSize: '10px', 
                background: '#f5f5f5', 
                padding: '10px',
                overflow: 'auto',
                maxHeight: '300px'
              }}>
                {JSON.stringify(archiveData, null, 2)}
              </pre>
            </details>
          </Box>
        )}
      </Paper>
    </Grid>
  );
}
